import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type {
  Paper,
  Keyword,
  AnalysisResults,
  Statistics,
  Theme,
  AppSettings,
  FilterState,
  ProcessingProgress
} from '../types';

interface AppState {
  // Theme
  theme: Theme;
  setTheme: (theme: Theme) => void;

  // Papers
  papers: Paper[];
  setPapers: (papers: Paper[]) => void;
  clearPapers: () => void;

  // Keywords
  inclusionKeywords: Keyword[];
  exclusionKeywords: Keyword[];
  addKeyword: (keyword: Omit<Keyword, 'id'>) => void;
  removeKeyword: (id: string) => void;
  updateKeyword: (id: string, text: string) => void;
  reorderKeywords: (type: 'inclusion' | 'exclusion', keywords: Keyword[]) => void;
  clearKeywords: (type?: 'inclusion' | 'exclusion') => void;
  importKeywords: (text: string) => void;

  // Analysis
  results: AnalysisResults | null;
  setResults: (results: AnalysisResults) => void;
  clearResults: () => void;

  // Statistics
  statistics: Statistics | null;

  // Processing
  isProcessing: boolean;
  progress: ProcessingProgress | null;
  setProgress: (progress: ProcessingProgress | null) => void;
  setIsProcessing: (isProcessing: boolean) => void;

  // Filter
  filter: FilterState;
  setFilter: (filter: Partial<FilterState>) => void;

  // Settings
  settings: AppSettings;
  updateSettings: (settings: Partial<AppSettings>) => void;

  // File info
  currentFileName: string | null;
  setCurrentFileName: (name: string | null) => void;

  // UI State
  activeTab: 'upload' | 'keywords' | 'results' | 'export';
  setActiveTab: (tab: 'upload' | 'keywords' | 'results' | 'export') => void;
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
}

const generateId = () => Math.random().toString(36).substring(2, 9);

export const useStore = create<AppState>()(
  persist(
    (set, get) => ({
      // Theme
      theme: 'system',
      setTheme: (theme) => {
        set({ theme });
        applyTheme(theme);
      },

      // Papers
      papers: [],
      setPapers: (papers) => set({ papers }),
      clearPapers: () => set({ papers: [], results: null, statistics: null }),

      // Keywords
      inclusionKeywords: [],
      exclusionKeywords: [],
      addKeyword: (keyword) => {
        const newKeyword = { ...keyword, id: generateId() };
        if (keyword.type === 'inclusion') {
          set((state) => ({
            inclusionKeywords: [...state.inclusionKeywords, newKeyword],
          }));
        } else {
          set((state) => ({
            exclusionKeywords: [...state.exclusionKeywords, newKeyword],
          }));
        }
      },
      removeKeyword: (id) => {
        set((state) => ({
          inclusionKeywords: state.inclusionKeywords.filter((k) => k.id !== id),
          exclusionKeywords: state.exclusionKeywords.filter((k) => k.id !== id),
        }));
      },
      updateKeyword: (id, text) => {
        set((state) => ({
          inclusionKeywords: state.inclusionKeywords.map((k) =>
            k.id === id ? { ...k, text } : k
          ),
          exclusionKeywords: state.exclusionKeywords.map((k) =>
            k.id === id ? { ...k, text } : k
          ),
        }));
      },
      reorderKeywords: (type, keywords) => {
        if (type === 'inclusion') {
          set({ inclusionKeywords: keywords });
        } else {
          set({ exclusionKeywords: keywords });
        }
      },
      clearKeywords: (type) => {
        if (!type) {
          set({ inclusionKeywords: [], exclusionKeywords: [] });
        } else if (type === 'inclusion') {
          set({ inclusionKeywords: [] });
        } else {
          set({ exclusionKeywords: [] });
        }
      },
      importKeywords: (text) => {
        const lines = text.split('\n').map((l) => l.trim()).filter(Boolean);
        let currentType: 'inclusion' | 'exclusion' | null = null;
        const inclusion: Keyword[] = [];
        const exclusion: Keyword[] = [];

        for (const line of lines) {
          const lower = line.toLowerCase();
          if (lower === 'inclusion') {
            currentType = 'inclusion';
          } else if (lower === 'exclusion') {
            currentType = 'exclusion';
          } else if (currentType) {
            const keyword: Keyword = {
              id: generateId(),
              text: line,
              type: currentType,
            };
            if (currentType === 'inclusion') {
              inclusion.push(keyword);
            } else {
              exclusion.push(keyword);
            }
          }
        }

        set({
          inclusionKeywords: inclusion,
          exclusionKeywords: exclusion,
        });
      },

      // Analysis
      results: null,
      setResults: (results) => {
        const stats = calculateStatistics(results, get().inclusionKeywords, get().exclusionKeywords);
        set({ results, statistics: stats });
      },
      clearResults: () => set({ results: null, statistics: null }),

      // Statistics
      statistics: null,

      // Processing
      isProcessing: false,
      progress: null,
      setProgress: (progress) => set({ progress }),
      setIsProcessing: (isProcessing) => set({ isProcessing }),

      // Filter
      filter: {
        searchQuery: '',
        selectedCategories: ['inclusion', 'exclusion', 'both', 'none'],
        sortBy: 'title',
        sortOrder: 'asc',
      },
      setFilter: (filter) =>
        set((state) => ({ filter: { ...state.filter, ...filter } })),

      // Settings
      settings: {
        theme: 'system',
        autoSave: true,
        highlightKeywords: true,
        caseSensitive: false,
        matchWholeWords: false,
        searchInAbstract: true,
        recentFiles: [],
      },
      updateSettings: (settings) =>
        set((state) => ({ settings: { ...state.settings, ...settings } })),

      // File info
      currentFileName: null,
      setCurrentFileName: (name) => set({ currentFileName: name }),

      // UI State
      activeTab: 'upload',
      setActiveTab: (tab) => set({ activeTab: tab }),
      sidebarOpen: true,
      setSidebarOpen: (open) => set({ sidebarOpen: open }),
    }),
    {
      name: 'papers-refiner-storage',
      partialize: (state) => ({
        theme: state.theme,
        inclusionKeywords: state.inclusionKeywords,
        exclusionKeywords: state.exclusionKeywords,
        settings: state.settings,
      }),
    }
  )
);

// Helper function to apply theme
function applyTheme(theme: Theme) {
  const root = document.documentElement;
  const systemDark = window.matchMedia('(prefers-color-scheme: dark)').matches;

  if (theme === 'dark' || (theme === 'system' && systemDark)) {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }
}

// Helper function to calculate statistics
function calculateStatistics(
  results: AnalysisResults,
  inclusionKeywords: Keyword[],
  exclusionKeywords: Keyword[]
): Statistics {
  const keywordCounts = new Map<string, number>();

  // Count keyword occurrences
  const allPapers = [
    ...results.inclusionOnly,
    ...results.exclusionOnly,
    ...results.both,
  ];

  for (const paper of allPapers) {
    for (const kw of paper.matchedInclusionKeywords) {
      keywordCounts.set(kw, (keywordCounts.get(kw) || 0) + 1);
    }
    for (const kw of paper.matchedExclusionKeywords) {
      keywordCounts.set(kw, (keywordCounts.get(kw) || 0) + 1);
    }
  }

  const keywordStats = [
    ...inclusionKeywords.map((k) => ({
      keyword: k.text,
      type: 'inclusion' as const,
      count: keywordCounts.get(k.text) || 0,
    })),
    ...exclusionKeywords.map((k) => ({
      keyword: k.text,
      type: 'exclusion' as const,
      count: keywordCounts.get(k.text) || 0,
    })),
  ].sort((a, b) => b.count - a.count);

  return {
    inclusionCount: results.inclusionOnly.length,
    exclusionCount: results.exclusionOnly.length,
    bothCount: results.both.length,
    noneCount: results.none.length,
    totalPapers: results.total,
    keywordStats,
  };
}

// Initialize theme on load
if (typeof window !== 'undefined') {
  const stored = localStorage.getItem('papers-refiner-storage');
  if (stored) {
    try {
      const { state } = JSON.parse(stored);
      if (state?.theme) {
        applyTheme(state.theme);
      }
    } catch (e) {
      applyTheme('system');
    }
  }
}
